import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { hoy, type Centavos } from '@abastece/shared';
import { esDeudaAnterior, type PagoProveedor } from '@abastece/core';
import { db } from '../db/dexie.js';
import { rangoDe } from '../lib/historial.js';
import {
  FORMAS_DE_PAGO_PROVEEDOR,
  SIN_FILTROS_PAGOS,
  VIGENCIAS,
  filtrosDePagos,
  pagoCoincide,
  resumenPagos,
  type ContextoPagos,
  type FiltroPagos,
  type PagoHecho,
  type Vigencia,
} from '../lib/historialDinero.js';
import { leerPagosHechos } from '../lib/leerPagos.js';
import { useMonedaDeLosLibros } from '../lib/libros.js';
import { usePuede } from '../lib/sesion.js';
import { AnularPago } from '../ui/Correcciones.js';
import { FilaMoneda } from '../ui/ElegirMoneda.js';
import { FilaPagoProveedor, diaDeLista } from '../ui/FilasDeDinero.js';
import { BuscarEnLista, Desplegable, FilaDesplegables, FiltroFechas, FiltrosEnLinea } from '../ui/FiltrosHistorial.js';
import { Ico } from '../ui/Iconos.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { useT } from '../i18n/index.js';

/** How many show at a time: the latest, and «Ver más» for the rest. */
const LOTE = 50;

/**
 * «Pagos a proveedores», all of them: every payment of a purchase or of an expense bought on
 * credit, the parts paid with a purchase among them, found by day, supplier, way of paying, the
 * invoice or the receipt. «Gastos» keeps the latest twenty, and voids one there as here.
 */
export function PagosProveedores() {
  const t = useT();
  const puedeAnular = usePuede('configurar');
  /** Córdobas or dollars, as «Gastos» is read (2026-10-13). */
  const libros = useMonedaDeLosLibros();
  const enFila = libros.moneda === 'USD' ? (c: Centavos) => libros.dinero(c) : undefined;
  const hoyEs = hoy();
  const pagos = useLiveQuery(() => leerPagosHechos(), [], undefined);
  const proveedores = useLiveQuery(() => db.proveedores.toArray(), [], []);
  const compras = useLiveQuery(() => db.compras.toArray(), [], []);
  const gastos = useLiveQuery(() => db.gastos.toArray(), [], []);
  const [filtro, setFiltro] = useState<FiltroPagos>(SIN_FILTROS_PAGOS);
  const [cuantos, setCuantos] = useState(LOTE);
  const [anulando, setAnulando] = useState<{ deuda: PagoHecho['deuda']; pago: PagoProveedor; nombre: string } | null>(null);

  const rango = rangoDe(filtro.periodo, hoyEs, filtro);
  const puestos = filtrosDePagos(filtro, rango);
  const filtrando = filtro.texto.trim() !== '' || puestos.length > 0;
  const cambiar = (cambio: Partial<FiltroPagos>) => {
    setFiltro((f) => ({ ...f, ...cambio }));
    setCuantos(LOTE);
  };

  const contexto = useMemo<ContextoPagos>(
    () => ({
      proveedores: new Map(proveedores.map((p) => [p.id, p.nombre])),
      compras: new Map(compras.map((c) => [c.id, c])),
      gastos: new Map(gastos.map((g) => [g.id, g])),
    }),
    [proveedores, compras, gastos],
  );
  const encontrados = useMemo(
    () => (pagos ?? []).filter((p) => pagoCoincide(p, filtro, rango, contexto)),
    // `rango` is worked out afresh each time: its two days are what it is.
    [pagos, filtro, rango.desde, rango.hasta, contexto],
  );
  const resumen = resumenPagos(encontrados);

  const nombreDe = (p: PagoHecho) => (p.proveedorId ? contexto.proveedores.get(p.proveedorId) : undefined) ?? t('gastos.nadieEnParticular');
  /** What the payment was for, how it went, and its receipt. */
  const detalle = (p: PagoHecho) => {
    const compra = p.deuda.tipo === 'compra' ? contexto.compras.get(p.deuda.id) : undefined;
    const gasto = p.deuda.tipo === 'gasto' ? contexto.gastos.get(p.deuda.id) : undefined;
    const para = compra
      ? esDeudaAnterior(compra)
        ? t('pagosProv.deudaAnterior')
        : compra.documento
          ? t('pagosProv.factura', { documento: compra.documento })
          : t('pagosProv.compraDel', { fecha: diaDeLista(compra.fecha) })
      : (gasto?.descripcion ?? '');
    return [para, t(`pago.${p.formaPago}`), p.referencia ?? ''].filter(Boolean).join(' · ');
  };

  return (
    <Pagina titulo={t('gastos.pagosAProveedores')} subtitulo={t('pagosProv.registrados', { n: pagos?.length ?? 0 })} volverA="/gastos" volverBusqueda={{ vista: 'pagos' }}>
      <FilaMoneda moneda={libros.moneda} onElegir={libros.elegir} tasa={libros.tasa} />
      {(filtrando || (pagos?.length ?? 0) > 0) && (
        <FiltrosEnLinea
          buscar={{ texto: filtro.texto, onTexto: (texto) => cambiar({ texto }), placeholder: t('pagosProv.buscar') }}
          hayPuestos={puestos.length > 0}
          onQuitarTodos={() => cambiar({ ...SIN_FILTROS_PAGOS, texto: filtro.texto })}
        >
          <FiltroFechas periodo={filtro.periodo} desde={filtro.desde} hasta={filtro.hasta} onCambio={cambiar} />
          <BuscarEnLista
            clave="proveedorId"
            etiqueta={t('compra.proveedor')}
            placeholder={t('historial.buscarProveedor')}
            opciones={proveedores}
            value={filtro.proveedorId}
            onChange={(proveedorId) => cambiar({ proveedorId })}
          />
          <FilaDesplegables>
            <Desplegable
              clave="forma"
              etiqueta={t('historial.forma')}
              value={filtro.forma}
              onChange={(v) => cambiar({ forma: v as FiltroPagos['forma'] })}
              elegido={filtro.forma ? t(`pago.${filtro.forma}`) : undefined}
            >
              <option value="">{t('historial.todas')}</option>
              {FORMAS_DE_PAGO_PROVEEDOR.map((f) => (
                <option key={f} value={f}>
                  {t(`pago.${f}`)}
                </option>
              ))}
            </Desplegable>
            <Desplegable
              clave="estado"
              etiqueta={t('historial.estado')}
              value={filtro.estado}
              onChange={(v) => cambiar({ estado: v as Vigencia })}
              elegido={filtro.estado === 'todos' ? undefined : t(`historial.vigencia.${filtro.estado}`)}
            >
              {VIGENCIAS.map((v) => (
                <option key={v} value={v}>
                  {t(`historial.vigencia.${v}`)}
                </option>
              ))}
            </Desplegable>
          </FilaDesplegables>
        </FiltrosEnLinea>
      )}

      {filtrando && encontrados.length > 0 && (
        <p className="tabular mb-3 text-sm text-slate-400" data-historial-resumen>
          {t('historial.encontrados', { n: resumen.n, total: libros.dinero(resumen.total) })}
        </p>
      )}

      {/* Nothing said while it is read: «nothing matches» was only «not read yet». */}
      {pagos === undefined ? null : encontrados.length === 0 ? (
        filtrando ? (
          <Vacio mensaje={t('pagosProv.ninguno')} icono="buscar">
            <button type="button" className="btn-secundario" onClick={() => cambiar(SIN_FILTROS_PAGOS)}>
              <Ico icono="reiniciar" />
              {t('historial.quitarBusqueda')}
            </button>
          </Vacio>
        ) : (
          <Vacio mensaje={t('pagosProv.vacio')} icono="pagar" />
        )
      ) : (
        <ul className="tarjeta flex flex-col divide-y divide-slate-800 py-1 text-sm" data-historial-lista>
          {encontrados.slice(0, cuantos).map((p) => {
            const nombre = nombreDe(p);
            return (
              <FilaPagoProveedor
                key={p.id}
                pago={p}
                nombre={nombre}
                detalle={detalle(p)}
                className="py-2.5"
                {...(enFila ? { dinero: enFila } : {})}
                {...(puedeAnular && p.pago && p.anulable ? { onAnular: () => setAnulando({ deuda: p.deuda, pago: p.pago!, nombre }) } : {})}
              />
            );
          })}
        </ul>
      )}
      {encontrados.length > cuantos && (
        <button type="button" className="btn-secundario mt-4 w-full" onClick={() => setCuantos((n) => n + LOTE)}>
          <Ico icono="desplegar" />
          {t('comun.verMas')}
        </button>
      )}

      {anulando && <AnularPago {...anulando} onCerrar={() => setAnulando(null)} />}
    </Pagina>
  );
}
